/**
 * Follow Button Component
 * Handles follow/unfollow actions with different states
 */

import { Loader2 } from "lucide-react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { useFollow } from "../hooks/useFollow"
import { useFollowActions } from "../hooks/useFollowActions"

interface FollowButtonProps {
  targetUserId: string
  variant?: "default" | "outline" | "ghost" | "primary"
  size?: "default" | "sm" | "lg"
  className?: string
  initialStatus?: { isFollowing: boolean; isPending: boolean }
}

export function FollowButton({
  targetUserId,
  variant = "outline",
  size = "sm",
  className = "",
  initialStatus,
}: FollowButtonProps) {
  const { t } = useTranslation("profile")
  const currentUser = useAuthStore(state => state.user)
  const [showLoginModal, setShowLoginModal] = useState(false)

  const useQueryResult = useFollow(targetUserId, !!initialStatus)

  const isFollowing = initialStatus?.isFollowing ?? useQueryResult.isFollowing
  const isPending = initialStatus?.isPending ?? useQueryResult.isPending
  const isLoading = initialStatus ? false : useQueryResult.isLoading
  const { follow, unfollow, isFollowing: isFollowingMutating, isUnfollowing } = useFollowActions()

  const isMutating = isFollowingMutating || isUnfollowing

  const handleFollow = () => {
    if (!currentUser) {
      setShowLoginModal(true)
      return
    }
    if (isFollowing) {
      unfollow(targetUserId)
    } else {
      follow(targetUserId)
    }
  }

  if (isLoading) {
    return (
      <Button variant={variant} size={size} className={className} disabled>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      </Button>
    )
  }

  if (isFollowing) {
    return (
      <>
        <Button
          variant={variant}
          size={size}
          className={className}
          onClick={handleFollow}
          disabled={isMutating}
        >
          {isMutating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <span>{t("followButton.unfollow")}</span>
          )}
        </Button>
        <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
      </>
    )
  }

  if (isPending) {
    return (
      <>
        <Button
          variant={variant}
          size={size}
          className={className}
          onClick={handleFollow}
          disabled={isMutating}
        >
          {isMutating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <span>{t("followButton.requested")}</span>
          )}
        </Button>
        <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
      </>
    )
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleFollow}
        disabled={isMutating}
      >
        {isMutating ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <span>{t("followButton.follow")}</span>
        )}
      </Button>
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </>
  )
}
